import { t as coreT } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useCallback, useRef } from 'react';
import UDFormTextarea from 'ud-form/components/input/textarea';
import { UDButton } from 'ud-ui/components';
import * as Yup from 'yup';

const MAX_MESSAGE_LENGTH = 4000;

export interface ChatComposerValues {
  message: string;
}

const validationSchema = Yup.object().shape({
  message: Yup.string()
    .trim()
    .required(() => coreT`Введите сообщение`)
    .max(MAX_MESSAGE_LENGTH, () => coreT`Сообщение не должно превышать ${MAX_MESSAGE_LENGTH} символов`),
});

interface Props {
  disabled?: boolean;
  onSubmit: (message: string) => Promise<void>;
}

const ChatComposer: React.FC<Props> = ({ disabled = false, onSubmit }) => {
  const { t } = useLingui();
  const containerRef = useRef<HTMLDivElement>(null);

  const focusInput = useCallback(() => {
    containerRef.current?.querySelector('textarea')?.focus();
  }, []);

  const handleSubmit = async (values: ChatComposerValues, helpers: FormikHelpers<ChatComposerValues>) => {
    const message = values.message.trim();
    if (!message) return;

    helpers.resetForm();
    requestAnimationFrame(focusInput);
    await onSubmit(message);
    requestAnimationFrame(focusInput);
  };

  return (
    <div ref={containerRef} className="w-full">
      <Formik<ChatComposerValues>
        initialValues={{ message: '' }}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleSubmit}
      >
        {({ values, submitForm, isSubmitting }) => {
          const isBusy = disabled || isSubmitting;

          return (
            <Form className="flex items-end gap-3">
              <UDFormTextarea
                name="message"
                containerProps={{ className: 'flex-1' }}
                placeholder={t`Например: Добавь команду Ястребы в турнир Первая лига`}
                rows={2}
                style={{ resize: 'vertical', minHeight: 44 }}
                autoFocus
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    if (values.message.trim() && !isBusy) {
                      submitForm();
                    }
                  }
                }}
              />
              <UDButton variant="primary" type="submit" loading={isBusy} disabled={!values.message.trim() || isBusy}>
                <Trans>Отправить</Trans>
              </UDButton>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ChatComposer;
